<script lang="ts" setup>
import { ref } from "vue";
import ModelError from "../components/ModelError.vue";
import RegistrationModel from "./RegisterModels";
import http from "../contact/http";
import logger from "../logger";
import { useI18n } from "vue-i18n";

const urlParams = new URLSearchParams(window.location.search);

const { t } = useI18n();
const error = ref(false);
const isBusy = ref(false);
const sent = ref(false);
const registration = ref(new RegistrationModel());
const model = registration.value.getModel();
const courseName = ref(urlParams.get("course"));
const currentStep = ref(1);

if (!courseName.value) window.location.replace("/en");

// Set courseId from URL parameter
registration.value.registration.courseName = courseName.value || "";

async function nextStep() {
  // Validate current step fields before proceeding
  const stepOneFields = ["name", "email", "courseName", "numberSeats"];
  let isValid = true;

  for (const field of stepOneFields) {
    const fieldModel = model.value[field];
    if (fieldModel && fieldModel.$validate) {
      await fieldModel.$validate();
      if (fieldModel.$invalid) {
        isValid = false;
      }
    }
  }

  if (isValid) {
    currentStep.value = 2;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

function previousStep() {
  currentStep.value = 1;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function onSubmit() {
  logger.log(`Starting Submit`);

  // reset
  sent.value = false;
  isBusy.value = false;
  error.value = false;

  try {
    // Validate
    if (model.value.$validate && (await model.value.$validate())) {
      // Save
      isBusy.value = true;

      logger.log(`Sending Registration...`, registration.value);
      const result = await http.register(registration.value.registration);
      
      if (result) {
        model.value.$reset();
        registration.value.reset();
        sent.value = true;
        currentStep.value = 1;
        logger.log(`Mail Sent`);
      } else {
        error.value = true;
        logger.log(`Mail failed`);
      }
    }
  } finally {
    isBusy.value = false;
  }
}
</script>

<template>
  <div>
    <div
      v-if="isBusy"
      class="w-full p-1 text-center text-gray-300 rounded mb-4"
    >
      <svg
        class="h-5 animate-spin fill-gray-300 inline-block"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      >
        <path
          d="M222.7 32.1c5 16.9-4.6 34.8-21.5 39.8C121.8 95.6 64 169.1 64 256c0 106 86 192 192 192s192-86 192-192c0-86.9-57.8-160.4-137.1-184.1c-16.9-5-26.6-22.9-21.5-39.8s22.9-26.6 39.8-21.5C434.9 42.1 512 140 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 140 77.1 42.1 182.9 10.6c16.9-5 34.8 4.6 39.8 21.5z"
        />
      </svg>
      {{ $t("sending") }}
    </div>

    <form
      method="post"
      @submit.prevent="onSubmit()"
      id="registration-form"
      novalidate
    >
      <p class="mb-0" id="error-msg"></p>
      <div id="simple-msg"></div>
      <div class="w-full lg:w-2/3 2xl:w-1/2 lg:mx-auto">
        <!-- Step Indicator -->
        <div class="flex items-center justify-center mb-8">
          <div class="flex items-center">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors"
              :class="
                currentStep >= 1
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'border-gray-400 text-gray-400'
              "
            >
              <span class="font-semibold">1</span>
            </div>
            <div
              class="w-16 h-1 mx-2 transition-colors"
              :class="currentStep >= 2 ? 'bg-amber-500' : 'bg-gray-300'"
            ></div>
            <div
              class="flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors"
              :class="
                currentStep >= 2
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'border-gray-400 text-gray-400'
              "
            >
              <span class="font-semibold">2</span>
            </div>
          </div>
        </div>

        <!-- Step 1: Registration Details -->
        <div v-if="currentStep === 1">
          <h2 class="text-2xl font-bold mb-6 text-center">
            {{ $t("registrationDetails") }}
          </h2>

          <div class="row-wrapper">
            <label class="form-label">{{ $t("courseName") }}</label>
            <input
              name="courseName"
              readonly
              v-model="model.courseName.$model"
              type="text"
              class="form-input"
              placeholder=""
            />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="name">{{ $t("name") }}</label>
            <input
              name="name"
              v-model="model.name.$model"
              type="text"
              class="form-input"
              placeholder="John Smith"
              autofocus
              :class="{ error: model.name.$error && model.name.$invalid }"
            />
            <ModelError :model="model.name" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="email">{{ $t("email") }}</label>
            <input
              name="email"
              v-model="model.email.$model"
              type="email"
              class="form-input"
              placeholder="you@aol.com"
              :class="{ error: model.$error && model.email.$invalid }"
            />
            <ModelError v-if="model.email.$error" :model="model.email" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="phoneNumber">{{
              $t("phoneNumber")
            }}</label>
            <input
              name="phoneNumber"
              v-model="model.phoneNumber.$model"
              type="tel"
              class="form-input"
              placeholder="+1 555-123-4567 (optional)"
              :class="{ error: model.$error && model.phoneNumber.$invalid }"
            />
            <ModelError :model="model.phoneNumber" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="numberSeats">{{
              $t("numberSeats")
            }}</label>
            <input
              name="numberSeats"
              v-model="model.numberSeats.$model"
              type="number"
              min="1"
              class="form-input"
              :class="{ error: model.$error && model.numberSeats.$invalid }"
            />
            <ModelError :model="model.numberSeats" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="comments">{{
              $t("comments")
            }}</label>
            <textarea
              name="comments"
              v-model="model.comments.$model"
              class="form-input h-24"
              :class="{ error: model.$error && model.comments.$invalid }"
            ></textarea>
            <ModelError v-if="model.comments.$error" :model="model.comments" />
          </div>

          <div class="row-wrapper flex justify-end">
            <button
              type="button"
              @click="nextStep"
              class="btn bg-amber-500 hover:bg-amber-600 border-amber-500 hover:border-amber-600 text-white rounded-md h-11 px-8 justify-center flex items-center"
            >
              {{ $t("next") }}
            </button>
          </div>
        </div>

        <!-- Step 2: Billing Details -->
        <div v-if="currentStep === 2">
          <h2 class="text-2xl font-bold mb-6 text-center">
            {{ $t("billingDetails") }}
          </h2>

          <div class="row-wrapper">
            <label class="form-label" for="company">{{
              $t("company")
            }}</label>
            <input
              name="company"
              v-model="model.billingDetail.company.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.company.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.company" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="billingContactName">{{
              $t("billingContactName")
            }}</label>
            <input
              name="billingContactName"
              v-model="model.billingDetail.billingContactName.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error:
                  model.$error &&
                  model.billingDetail.billingContactName.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.billingContactName" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="billingEmail">{{
              $t("billingEmail")
            }}</label>
            <input
              name="billingEmail"
              v-model="model.billingDetail.billingEmail.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error:
                  model.$error && model.billingDetail.billingEmail.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.billingEmail" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="vatNumber">{{
              $t("vatNumber")
            }}</label>
            <input
              name="vatNumber"
              v-model="model.billingDetail.vatNumber.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.vatNumber.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.vatNumber" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="address1">{{
              $t("address1")
            }}</label>
            <input
              name="address1"
              v-model="model.billingDetail.address1.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.address1.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.address1" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="address2">{{
              $t("address2")
            }}</label>
            <input
              name="address2"
              v-model="model.billingDetail.address2.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.address2.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.address2" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="address3">{{
              $t("address3")
            }}</label>
            <input
              name="address3"
              v-model="model.billingDetail.address3.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.address3.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.address3" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="cityTown">{{
              $t("cityTown")
            }}</label>
            <input
              name="cityTown"
              v-model="model.billingDetail.cityTown.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.cityTown.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.cityTown" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="stateRegion">{{
              $t("stateRegion")
            }}</label>
            <input
              name="stateRegion"
              v-model="model.billingDetail.stateRegion.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.stateRegion.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.stateRegion" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="postalCode">{{
              $t("postalCode")
            }}</label>
            <input
              name="postalCode"
              v-model="model.billingDetail.postalCode.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.postalCode.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.postalCode" />
          </div>

          <div class="row-wrapper">
            <label class="form-label" for="country">{{ $t("country") }}</label>
            <input
              name="country"
              v-model="model.billingDetail.country.$model"
              type="text"
              class="form-input"
              placeholder="(optional)"
              :class="{
                error: model.$error && model.billingDetail.country.$invalid,
              }"
            />
            <ModelError :model="model.billingDetail.country" />
          </div>

          <div class="row-wrapper flex justify-between gap-4">
            <button
              type="button"
              @click="previousStep"
              class="btn bg-gray-500 hover:bg-gray-600 border-gray-500 hover:border-gray-600 text-white rounded-md h-11 px-8 justify-center flex items-center"
            >
              {{ $t("previous") }}
            </button>
            <button
              type="submit"
              id="submit"
              name="send"
              :disabled="(model.$dirty && model.$invalid) || isBusy"
              class="btn bg-amber-500 hover:bg-amber-600 disabled:bg-amber-200 disabled:text-gray-500 border-amber-500 hover:border-amber-600 text-white rounded-md h-11 px-8 justify-center flex items-center"
            >
              {{ $t("register") }}
            </button>
          </div>
        </div>
      </div>
      <div>
        <div
          v-if="sent"
          class="text-lg py-2 text-primary text-center"
          v-html="t('registrationConfirmed')"
        ></div>
        <div
          v-if="error"
          class="text-lg py-2 text-warning text-center"
          v-html="t('errorRegistration')"
        ></div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.error {
  border-color: rgba(255, 0, 0, 0.5) !important;
  /* border-width: 1px !important; */
}

.form-input {
  @apply w-full py-2 px-3  read-only:!bg-slate-700 read-only:!text-slate-500 placeholder:italic border border-inherit dark:border-gray-800 dark:bg-slate-900 dark:text-slate-200 rounded h-10 outline-none bg-transparent focus:border-amber-500/50 dark:focus:border-amber-500/50 focus:shadow-none focus:ring-0 text-lg;
}

.form-label {
  @apply ml-1 text-lg text-slate-500;
}

.row-wrapper {
  @apply lg:col-span-6 lg:col-start-4 mb-5;
}
</style>
